/** XeAgents.ai**/
import { useEffect, useState } from "react";
import { COUNTRIES } from "@shared/countries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Loader2, UserPlus, Check, AlertCircle, ArrowRight, Mail, Percent, Wrench, Rocket } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const PILOT_BASE = "/api/admin/pilot-requests";

/**
 * Countries an owner account can be opened in. Kept as data rather than inline
 * markup so a pilot request's country can be checked against it before being
 * pre-filled - a request from outside this list falls back to the default.
 */
// The country list lives in shared/countries.ts - this was a second copy of the
// same ten that signup declares inline.
const ONBOARDING_COUNTRIES = COUNTRIES;

interface PilotRequest {
  id: string;
  fullName: string;
  workEmail: string;
  /** Null on rows the Cal.com webhook created from a demo booking. */
  company: string | null;
  phone: string | null;
  teamSize: string | null;
  goals: string | null;
  country: string | null;
  source: "form" | "demo_booking";
  status: "new" | "contacted" | "converted" | "declined";
  createdAt: string;
}

interface OnboardingFormData {
  name: string;
  email: string;
  organizationName: string;
  country: string;
  discountPercentage: number;
  setupFee: number | null;
}

interface CreatedOwnerResult {
  success: boolean;
  temporaryPassword: string;
  emailSent: boolean;
  emailError?: string;
  setupFeePaymentLink?: string; // If setup fee required, this contains the payment link
  stripeError?: string; // If Stripe session creation failed
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    planType: string;
    credits: number;
    isActive: boolean;
    discountPercentage?: number;
    setupFee?: string;
    setupFeeStatus?: string;
  };
}

interface OwnerOnboardingProps {
  /** A pilot request to pre-fill and select as soon as it loads - set when a super admin clicks a row on the Pilot Requests tab. */
  initialPilotRequestId?: string | null;
  /** Called once the id above has been applied, so the caller can clear it and not re-apply on every render. */
  onInitialPilotRequestConsumed?: () => void;
}

export default function OwnerOnboarding({
  initialPilotRequestId,
  onInitialPilotRequestConsumed,
}: OwnerOnboardingProps = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedPilotRequestId, setSelectedPilotRequestId] = useState<string>("");
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [createdResult, setCreatedResult] = useState<CreatedOwnerResult | null>(null);
  const [formData, setFormData] = useState<OnboardingFormData>({
    name: "",
    email: "",
    organizationName: "",
    country: "US",
    discountPercentage: 0,
    setupFee: null,
  });

  // Pilot requests not yet turned into an owner account - the dropdown below
  // offers these, and an onboarded one is marked 'converted' server-side.
  const pilotRequestsQuery = useQuery<{ items: PilotRequest[] }>({
    queryKey: [PILOT_BASE],
    queryFn: async () => {
      const response = await apiRequest("GET", PILOT_BASE);
      return response.json();
    },
  });

  const unonboardedPilotRequests = (pilotRequestsQuery.data?.items ?? []).filter(
    (r) => r.status !== "converted",
  );

  function applyPilotRequest(id: string) {
    setSelectedPilotRequestId(id);
    if (!id) return;

    const request = unonboardedPilotRequests.find((r) => r.id === id);
    if (!request) return;

    setFormData((prev) => ({
      ...prev,
      name: request.fullName,
      email: request.workEmail,
      // A demo-only lead has no company on file, so the org falls back to the
      // person's name rather than being created as "null's Organization".
      organizationName: request.company || `${request.fullName}'s Organization`,
      // The request's country is free-form relative to this list, so only take
      // it when it is one we can actually bill in.
      country: ONBOARDING_COUNTRIES.some((c) => c.value === request.country)
        ? (request.country as string)
        : prev.country,
    }));
  }

  // Arriving here from a clicked row on the Pilot Requests tab: apply it as soon
  // as the list is available, then tell the caller so it isn't re-applied.
  useEffect(() => {
    if (!initialPilotRequestId || pilotRequestsQuery.isLoading) return;
    applyPilotRequest(initialPilotRequestId);
    setShowCreateDialog(true);
    onInitialPilotRequestConsumed?.();
  }, [initialPilotRequestId, pilotRequestsQuery.isLoading]);

  // Resend credentials mutation - same approach as UserManagement
  const resendCredentialsMutation = useMutation({
    mutationFn: async (userId: string) => {
      const response = await apiRequest("POST", `/api/admin/users/${userId}/resend-credentials`, {});
      return response.json();
    },
    onSuccess: (data) => {
      if (data.emailSent) {
        toast({
          title: "Credentials Sent",
          description: "New temporary credentials have been emailed to the owner.",
        });
        // Update the result to reflect email was sent
        if (createdResult) {
          setCreatedResult({ ...createdResult, emailSent: true, emailError: undefined });
        }
      } else {
        toast({
          title: "Email Not Sent",
          description: "New password was generated but email could not be sent. Check SMTP settings.",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Create owner mutation
  const createOwnerMutation = useMutation({
    mutationFn: async (data: OnboardingFormData) => {
      // Generate a random temporary password (16 chars hex)
      const randomPassword = Array.from(crypto.getRandomValues(new Uint8Array(8)))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      const res = await apiRequest("POST", "/api/admin/users", {
        email: data.email,
        password: randomPassword,
        name: data.name,
        role: "owner",
        planType: "free",
        isActive: data.setupFee && data.setupFee > 0 ? false : true, // Inactive if setup fee required
        organizationName: data.organizationName,
        country: data.country,
        discountPercentage: data.discountPercentage,
        setupFee: data.setupFee,
        pilotRequestId: selectedPilotRequestId || undefined,
      });

      const result = await res.json();

      // Note: Organization is automatically created by the backend
      // No need for a separate API call

      return result as CreatedOwnerResult;
    },
    onSuccess: (result) => {
      setCreatedResult(result);
      setShowCreateDialog(false);
      setShowResultDialog(true);
      setFormData({
        name: "",
        email: "",
        organizationName: "",
        country: "US",
        discountPercentage: 0,
        setupFee: null,
      });
      // The onboarded request is now 'converted' server-side - refetch so it
      // drops out of both this dropdown and the Pilot Requests tab.
      if (selectedPilotRequestId) {
        queryClient.invalidateQueries({ queryKey: [PILOT_BASE] });
        setSelectedPilotRequestId("");
      }
      toast({
        title: "Owner Created",
        description: result.setupFeePaymentLink
          ? `Account created. Setup fee payment link sent to ${result.user.email}`
          : `Successfully created account for ${result.user.email}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast({ title: "Validation Error", description: "Full name is required", variant: "destructive" });
      return;
    }
    if (!formData.organizationName.trim()) {
      toast({ title: "Validation Error", description: "Organization name is required", variant: "destructive" });
      return;
    }
    if (!formData.email.trim()) {
      toast({ title: "Validation Error", description: "Email is required", variant: "destructive" });
      return;
    }
    createOwnerMutation.mutate(formData);
  };


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Owner Onboarding</h3>
          <p className="text-sm text-muted-foreground">
            Create new owner accounts with auto-generated credentials and organization setup
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} className="flex items-center gap-2 w-full sm:w-auto">
          <UserPlus className="h-4 w-4" />
          Onboard New Owner
        </Button>
      </div>

      {/* Info Card */}
      <Card>
        <CardHeader className="pb-3 sm:pb-6">
          <CardTitle className="text-sm sm:text-base">How Owner Onboarding Works</CardTitle>
          <CardDescription className="text-xs sm:text-sm">
            Quick overview of the onboarding process
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:gap-4 grid-cols-1 md:grid-cols-3">
            <div className="flex flex-col gap-2 rounded-lg border p-3 sm:p-4">
              <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs sm:text-sm">1</div>
              <h4 className="font-medium text-sm sm:text-base">Create Account</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Fill in the owner&apos;s details. A temporary password is auto-generated and emailed.
              </p>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border p-3 sm:p-4">
              <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs sm:text-sm">2</div>
              <h4 className="font-medium text-sm sm:text-base">Owner Logs In</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Owner uses the temporary credentials to sign in and selects a plan on first login.
              </p>
            </div>
            <div className="flex flex-col gap-2 rounded-lg border p-3 sm:p-4">
              <div className="flex h-7 w-7 sm:h-8 sm:w-8 items-center justify-center rounded-full bg-primary/10 text-primary font-bold text-xs sm:text-sm">3</div>
              <h4 className="font-medium text-sm sm:text-base">Organization Ready</h4>
              <p className="text-xs sm:text-sm text-muted-foreground">
                An organization is automatically created and assigned. Credits come from the billing plan.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Create Owner Dialog - Reuses signup page UI pattern */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="w-[95vw] max-w-[440px] p-0 gap-0 overflow-hidden rounded-2xl max-h-[90vh] overflow-y-auto">
          {/* Header matching signup style */}
          <div className="p-4 sm:p-6 pb-4 border-b bg-muted/30">
            <div className="space-y-3">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold">Onboard New Owner</h2>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  Create a new owner account with organization. Password is auto-generated.
                </p>
              </div>
            </div>
          </div>

          {/* Form matching signup field order & style */}
          <div className="p-4 sm:p-6">
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              {unonboardedPilotRequests.length > 0 && (
                <div className="space-y-1.5 sm:space-y-2 pb-1 border-b">
                  <Label htmlFor="owner-pilot-request" className="flex items-center gap-2 text-sm">
                    <Rocket className="h-3.5 w-3.5" />
                    Onboard from pilot request
                  </Label>
                  <Select
                    value={selectedPilotRequestId}
                    onValueChange={applyPilotRequest}
                  >
                    <SelectTrigger className="h-10 sm:h-11" id="owner-pilot-request" data-testid="select-owner-pilot-request">
                      <SelectValue placeholder="Start from scratch" />
                    </SelectTrigger>
                    <SelectContent>
                      {unonboardedPilotRequests.map((request) => (
                        <SelectItem key={request.id} value={request.id}>
                          {request.company || request.fullName}
                          {request.company ? ` — ${request.fullName}` : " (demo only)"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Picking one fills the fields below and marks the request converted once the owner is created.
                  </p>
                </div>
              )}

              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="owner-name" className="text-sm">Full Name</Label>
                <Input
                  id="owner-name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="h-10 sm:h-11"
                />
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="owner-org" className="text-sm">Organization Name</Label>
                <Input
                  id="owner-org"
                  type="text"
                  placeholder="Acme Inc."
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  required
                  className="h-10 sm:h-11"
                />
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="owner-country" className="text-sm">Country</Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData({ ...formData, country: value })}
                >
                  <SelectTrigger className="h-10 sm:h-11" id="owner-country">
                    <SelectValue placeholder="Select country" />
                  </SelectTrigger>
                  <SelectContent>
                    {ONBOARDING_COUNTRIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5 sm:space-y-2">
                <Label htmlFor="owner-email" className="text-sm">Email</Label>
                <Input
                  id="owner-email"
                  type="email"
                  placeholder="you@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-10 sm:h-11"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="owner-discount" className="flex items-center gap-2 text-sm">
                    <Percent className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Discount % (Optional)
                  </Label>
                  <Input
                    id="owner-discount"
                    type="number"
                    min="0"
                    max="100"
                    placeholder="0"
                    value={formData.discountPercentage || ""}
                    onChange={(e) => setFormData({ ...formData, discountPercentage: parseInt(e.target.value, 10) || 0 })}
                    className="h-10 sm:h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    Discount on subscription plans (0-100%)
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="owner-setup-fee" className="flex items-center gap-2 text-sm">
                    <Wrench className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    Setup Fee ({formData.country === "IN" ? "₹" : "$"})
                  </Label>
                  <Input
                    id="owner-setup-fee"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.setupFee || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFormData({ ...formData, setupFee: val === '' ? null : parseFloat(val) || 0 });
                    }}
                    className="h-10 sm:h-11"
                  />
                  <p className="text-xs text-muted-foreground">
                    One-time setup/development fee
                  </p>
                </div>
              </div>

              {/* Info note about auto-generated password */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950 p-2.5 sm:p-3">
                <p className="text-[11px] sm:text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                  <strong>Role:</strong> Owner <span className="hidden sm:inline">&nbsp;|&nbsp;</span><br className="sm:hidden" />
                  <strong>Plan:</strong> Free (owner selects plan on first login)
                  <br />
                  {formData.setupFee && formData.setupFee > 0 ? (
                    <>Setup fee payment link will be sent. Account activates after payment.</>
                  ) : (
                    <>A temporary password will be auto-generated and emailed to the owner.</>
                  )}
                </p>
              </div>

              <Button
                type="submit"
                className="w-full h-10 sm:h-11 bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:hover:bg-slate-200 text-white dark:text-slate-900 text-sm sm:text-base"
                disabled={createOwnerMutation.isPending || !formData.name.trim() || !formData.organizationName.trim() || !formData.email.trim()}
              >
                {createOwnerMutation.isPending ? (
                  <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin" />
                ) : (
                  <>
                    Create Owner Account
                    <ArrowRight className="ml-2 h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </>
                )}
              </Button>
            </form>
          </div>
        </DialogContent>
      </Dialog>

      {/* Result Dialog - Success confirmation */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="w-[95vw] max-w-[500px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              Owner Account Created
            </DialogTitle>
            <DialogDescription>
              The account has been created successfully.
            </DialogDescription>
          </DialogHeader>

          {createdResult && (
            <div className="space-y-3 sm:space-y-4 py-3 sm:py-4">
              <div className="rounded-lg border p-3 sm:p-4 space-y-2.5 sm:space-y-3">
                <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Name</span>
                  <span className="font-medium text-sm sm:text-base truncate">{createdResult.user.name}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Email</span>
                  <span className="font-medium text-sm sm:text-base truncate">{createdResult.user.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Role</span>
                  <span className="font-medium text-sm sm:text-base">Owner</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                  <span className="text-xs sm:text-sm text-muted-foreground">Plan</span>
                  <span className="font-medium text-sm sm:text-base capitalize">Free</span>
                </div>
                {createdResult.user.discountPercentage && createdResult.user.discountPercentage > 0 && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                    <span className="text-xs sm:text-sm text-muted-foreground">Discount</span>
                    <span className="font-medium text-sm sm:text-base text-green-600">{createdResult.user.discountPercentage}% off</span>
                  </div>
                )}
                {createdResult.user.setupFee && parseFloat(createdResult.user.setupFee) > 0 && (
                  <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2">
                    <span className="text-xs sm:text-sm text-muted-foreground">Setup Fee</span>
                    <span className="font-medium text-sm sm:text-base">
                      {parseFloat(createdResult.user.setupFee).toLocaleString()}
                      {createdResult.user.setupFeeStatus === "pending" && (
                        <span className="ml-2 text-amber-600 text-xs">(Pending)</span>
                      )}
                      {createdResult.user.setupFeeStatus === "paid" && (
                        <span className="ml-2 text-green-600 text-xs">(Paid)</span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Setup Fee Payment Link */}
              {createdResult.setupFeePaymentLink && (
                <Alert className="border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950">
                  <Wrench className="h-4 w-4 text-amber-600 shrink-0" />
                  <AlertDescription className="w-full">
                    <p className="font-medium mb-1.5 sm:mb-2 text-amber-700 dark:text-amber-300 text-sm">
                      Setup Fee Payment Required
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                      The account will be activated after setup fee payment is completed.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input value={createdResult.setupFeePaymentLink} readOnly className="text-xs flex-1" />
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full sm:w-auto"
                        onClick={() => {
                          navigator.clipboard.writeText(createdResult.setupFeePaymentLink!);
                          toast({ title: "Copied", description: "Payment link copied to clipboard" });
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* Stripe Error - Setup fee configured but payment link couldn't be created */}
              {createdResult.stripeError && !createdResult.setupFeePaymentLink && createdResult.user.setupFee && parseFloat(createdResult.user.setupFee) > 0 && (
                <Alert className="border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950">
                  <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
                  <AlertDescription className="w-full">
                    <p className="font-medium mb-1.5 text-red-700 dark:text-red-300 text-sm">
                      Setup Fee Payment Link Failed
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-2">
                      User was created but the Stripe payment link couldn&apos;t be generated: {createdResult.stripeError}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      You can manually activate the user from User Management after receiving payment.
                    </p>
                  </AlertDescription>
                </Alert>
              )}

              {createdResult.emailSent ? (
                <Alert>
                  <Mail className="h-4 w-4 text-green-600 shrink-0" />
                  <AlertDescription className="text-xs sm:text-sm">
                    {createdResult.setupFeePaymentLink ? (
                      <>Setup fee payment link has been emailed to <strong className="break-all">{createdResult.user.email}</strong>. Account will activate after payment.</>
                    ) : (
                      <>Login credentials have been emailed to <strong className="break-all">{createdResult.user.email}</strong>. The owner will receive their temporary password via email and must change it on first login.</>
                    )}
                  </AlertDescription>
                </Alert>
              ) : (
                <Alert>
                  <AlertCircle className="h-4 w-4 text-amber-600 shrink-0" />
                  <AlertDescription>
                    <p className="font-medium mb-1 text-amber-700 dark:text-amber-300 text-xs sm:text-sm">
                      Email was not sent.{createdResult.emailError ? ` Reason: ${createdResult.emailError}` : ' SMTP may not be configured.'}
                    </p>
                    <p className="text-xs sm:text-sm text-muted-foreground mb-2 sm:mb-3">
                      {createdResult.setupFeePaymentLink
                        ? "Share the payment link above manually with the customer."
                        : "Configure SMTP in Settings → Master first, then click Resend Credentials below to email the owner their login details."}
                    </p>
                    {!createdResult.setupFeePaymentLink && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 w-full sm:w-auto"
                        onClick={() => resendCredentialsMutation.mutate(createdResult.user.id)}
                        disabled={resendCredentialsMutation.isPending}
                      >
                        {resendCredentialsMutation.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                        Resend Credentials
                      </Button>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowResultDialog(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}